import React, { useState, useCallback, useRef, useEffect } from 'react';
import Header from './components/Header';
import PatientInfoCard from './components/PatientInfoCard';
import TestSection from './components/TestSection';
import SummaryView from './components/SummaryView';
import LoadingScreen from './components/LoadingScreen';
import Footer from './components/Footer';
import { PATIENT_INFO, LAB_REPORT_DATA } from './constants';
import { PATIENT_INFO_MDC, LAB_REPORT_DATA_MDC } from './constants-mdc';
import MdcReport from './components/MdcReport';
import { generateReportSummary } from './services/geminiService';
import { SparklesIcon, FileDownIcon, ChevronDownIcon, DalanyLogo } from './components/icons';
import type { PatientInfo, ReportSectionData } from './types';


const GenerateSummaryButton: React.FC<{
  onGenerate: (language: string) => void;
  isLoading: boolean;
}> = ({ onGenerate, isLoading }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const languages = {
    en: 'English',
    ku: 'Kurdish (Sorani)',
    ar: 'Arabic',
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const handleSelectLanguage = (langCode: string) => {
    onGenerate(langCode);
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block text-left" ref={wrapperRef}>
      <div>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={isLoading}
          className="flex items-center justify-center gap-2 bg-brand text-white font-semibold py-2 px-4 rounded-lg hover:bg-brand-dark transition-colors disabled:bg-brand/70 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand shadow-sm"
          id="menu-button"
          aria-expanded="true"
          aria-haspopup="true"
        >
          <SparklesIcon className="h-4 w-4" />
          <span className="font-rabar-bold">زیرەکی دەلانی</span>
          <span className="text-white/80">/</span>
          <span>Dalany AI</span>
          <ChevronDownIcon className="-mr-1 h-5 w-5" />
        </button>
      </div>

      {isOpen && (
        <div
          className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="menu-button"
        >
          <div className="py-1" role="none">
            {Object.entries(languages).map(([code, name]) => (
              <a
                href="#"
                key={code}
                onClick={(e) => {
                    e.preventDefault();
                    handleSelectLanguage(code);
                }}
                className="text-slate-700 block px-4 py-2 text-sm hover:bg-slate-100"
                role="menuitem"
              >
                {name}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const OriginalReport: React.FC<{ patientInfo: PatientInfo, reportData: ReportSectionData[] }> = ({ patientInfo, reportData }) => (
    <main className="a4-page-container relative">
       <div className="absolute inset-0 flex items-center justify-center z-0 opacity-10 pointer-events-none">
          <DalanyLogo className="w-96 h-96 text-slate-400" />
        </div>
       <div className="relative z-10">
          <table className="report-table w-full">
            <Header />
            <Footer />
            <tbody>
              <tr>
                <td>
                  <PatientInfoCard patientInfo={patientInfo} />
                  <div className="mt-8">
                      <h2 className="text-xl font-bold text-slate-800 mb-4 border-b border-slate-200/80 pb-2">
                        Laboratory Results
                      </h2>
                      {reportData.map((section, index) => (
                          <TestSection key={index} section={section} />
                      ))}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
    </main>
);

const App: React.FC = () => {
  const [summary, setSummary] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'report' | 'summary'>('report');
  const [activeReport, setActiveReport] = useState<'mdc' | 'dalany'>('mdc');

  const handleGenerateSummary = useCallback(async (language: string) => {
    setIsLoading(true);
    setError(null);
    setSummary('');

    const patientInfo = activeReport === 'dalany' ? PATIENT_INFO : PATIENT_INFO_MDC;
    const reportData = activeReport === 'dalany' ? LAB_REPORT_DATA : LAB_REPORT_DATA_MDC;

    const result = await generateReportSummary(patientInfo, reportData, language);
    
    if (result.startsWith("An error occurred")) {
        setError(result);
    } else {
        setSummary(result);
    }

    setIsLoading(false);
    setView('summary');
  }, [activeReport]);
  
  const handlePrint = () => {
    window.print();
  };

  if (view === 'summary') {
    return <SummaryView summary={summary} error={error} onBack={() => setView('report')} />;
  }

  const ReportToggleButton: React.FC<{ type: 'dalany' | 'mdc', name: string }> = ({ type, name }) => (
    <button
        onClick={() => setActiveReport(type)}
        className={`py-2 px-4 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400 first:rounded-l-lg last:rounded-r-lg ${
            activeReport === type 
            ? 'bg-slate-700 text-white z-10' 
            : 'text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 -ml-px'
        }`}
    >
        {name}
    </button>
  );

  return (
    <>
      {isLoading && <LoadingScreen />}
      <div className="min-h-screen bg-slate-100 font-sans">
        
        <div className="max-w-5xl mx-auto py-6 flex justify-between items-center gap-3 no-print px-4 sm:px-6 md:px-0">
            <div>
                 <span className="text-sm font-medium text-slate-600 mr-3">Switch Report Style:</span>
                 <div className="inline-flex rounded-lg shadow-sm">
                    <ReportToggleButton type="mdc" name="New Style" />
                    <ReportToggleButton type="dalany" name="Original Style" />
                 </div>
            </div>
            <div className="flex items-center gap-3">
                <button
                    onClick={handlePrint}
                    className="flex items-center gap-2 py-2 px-4 rounded-lg text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 hover:border-slate-400 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400 text-sm font-semibold shadow-sm"
                    aria-label="Export Report as PDF"
                >
                    <FileDownIcon className="h-4 w-4"/>
                    <span>Export PDF</span>
                </button>
                <GenerateSummaryButton onGenerate={handleGenerateSummary} isLoading={isLoading} />
            </div>
        </div>

        {activeReport === 'dalany' 
            ? <OriginalReport patientInfo={PATIENT_INFO} reportData={LAB_REPORT_DATA} /> 
            : <MdcReport patientInfo={PATIENT_INFO_MDC} reportData={LAB_REPORT_DATA_MDC} />
        }
      </div>
    </>
  );
};

export default App;